import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, FolderOpen } from "lucide-react";
import { Section, Reveal, Eyebrow } from "@/components/primitives";
import ShowroomCarousel from "@/components/ShowroomCarousel";
import { api } from "@/lib/apiClient";

const filters = [
  { key: "all", label: "Tutti" },
  { key: "siti-web", label: "Siti Web" },
  { key: "social", label: "Social" },
  { key: "marketing", label: "Marketing" },
];

const categoryLabel = {
  "siti-web": "Sito Web",
  social: "Social",
  marketing: "Marketing",
};

export default function Portfolio() {
  const [active, setActive] = useState("all");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAny, setHasAny] = useState(false);

  useEffect(() => {
    api
      .get("/projects")
      .then((res) => setHasAny(res.data.length > 0))
      .catch(() => setHasAny(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/projects${active !== "all" ? `?category=${active}` : ""}`)
      .then((res) => setProjects(res.data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [active]);

  return (
    <div data-testid="portfolio-page">
      <Section className="pt-20 pb-6">
        <Reveal>
          <Eyebrow>I nostri lavori</Eyebrow>
          <h1 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-3xl">
            Siti fatti per attività come la tua.
          </h1>
          <p className="mt-6 text-lg text-ink/70 max-w-2xl">
            Dai un'occhiata ad alcuni dei siti che abbiamo realizzato. Il
            prossimo potrebbe essere il tuo.
          </p>
        </Reveal>
      </Section>

      {/* Showroom carousel — anteprime dimostrative */}
      <ShowroomCarousel />

      {/* Progetti gestiti dall'admin (mostrati solo se presenti) */}
      {(hasAny || active !== "all") && (
        <Section className="pb-24 pt-6 border-t border-ink/10">
          <Reveal>
            <h2 className="font-display font-bold text-2xl lg:text-3xl tracking-tight text-ink">
              Altri progetti
            </h2>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActive(f.key)}
                data-testid={`portfolio-filter-${f.key}`}
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${
                  active === f.key
                    ? "bg-coral text-white"
                    : "bg-white text-ink hover:bg-ink hover:text-beige"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="mt-10">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
                ))}
              </div>
            ) : projects.length === 0 ? (
              <div className="bg-white rounded-2xl py-16 text-center shadow-[0_4px_24px_rgba(0,0,0,0.04)]" data-testid="portfolio-empty">
                <FolderOpen className="mx-auto text-coral" size={40} />
                <p className="mt-4 text-ink/60">Nessun progetto in questa categoria.</p>
              </div>
            ) : (
              <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence mode="popLayout">
                  {projects.map((p) => (
                    <motion.div
                      key={p.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.4 }}
                      data-testid={`portfolio-card-${p.id}`}
                      className="group bg-white rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
                    >
                      <div className="overflow-hidden h-56 bg-beige">
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-ink/20">
                            <FolderOpen size={40} />
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <span className="text-xs font-semibold uppercase tracking-widest text-coral">
                          {categoryLabel[p.category] || p.category}
                        </span>
                        <h3 className="mt-2 font-display font-bold text-xl text-ink">{p.title}</h3>
                        {p.client && <p className="text-sm text-ink/40">{p.client}{p.year ? ` · ${p.year}` : ""}</p>}
                        <p className="mt-3 text-ink/60 text-sm leading-relaxed line-clamp-3">{p.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </Section>
      )}

      {/* CTA finale */}
      <Section className="pb-24">
        <Reveal>
          <div className="bg-ink text-beige rounded-3xl px-8 py-14 lg:p-16 text-center">
            <h2 className="font-display font-bold text-2xl lg:text-4xl tracking-tight max-w-2xl mx-auto">
              Vuoi un sito così anche tu?
            </h2>
            <p className="mt-4 text-beige/60 max-w-xl mx-auto">
              Ti facciamo vedere una bozza del tuo sito, gratis e senza impegno.
            </p>
            <Link
              to="/contatti"
              data-testid="portfolio-cta"
              className="mt-8 inline-flex items-center gap-2 bg-coral text-white rounded-full px-8 py-4 font-semibold hover:bg-[#e04047] transition-colors"
            >
              Richiedi la tua bozza gratis <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
